/* import __COLOCATED_TEMPLATE__ from './groups-empty-state.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type PlaygroundApiService from 'embercom/services/playground-api';
import {
  ExtractionType,
  type QuestionGenerationUpdateEvent,
} from 'embercom/lib/fin-playground/question-generation';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';

export default class GroupsEmptyState extends Component {
  @service declare playgroundApi: PlaygroundApiService;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare realTimeEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;

  @tracked showUploadCSVModal = false;
  @tracked isAddingQuestions = false;
  @tracked showExceedingQuestionLimitBanner = false;

  constructor(owner: unknown, args: {}) {
    super(owner, args);
    this.realTimeEventService.on(
      'FinPlaygroundQuestionGenerationUpdate',
      this,
      '_handleQuestionGenerationResponse',
    );
  }

  willDestroy() {
    super.willDestroy();
    this.realTimeEventService.off(
      'FinPlaygroundQuestionGenerationUpdate',
      this,
      '_handleQuestionGenerationResponse',
    );
  }

  get hasAvailableExtractions() {
    return (
      this.playgroundSummary?.questionGenerationSummary?.availableExtractions?.all ||
      this.playgroundSummary?.questionGenerationSummary?.availableExtractions?.lowCsat ||
      this.playgroundSummary?.questionGenerationSummary?.availableExtractions?.handedToTeammate
    );
  }

  get playgroundSummary() {
    return this.playgroundApi.playgroundSummary;
  }

  get questionLimit() {
    return this.playgroundSummary?.questionLimit ?? 50;
  }

  get routeToTransitionTo() {
    return this.appService.app.canUseStandalone
      ? 'apps.app.standalone.testing.test'
      : 'apps.app.automation.fin-ai-agent.testing.test';
  }

  @action
  updateShowExceedingQuestionLimitBanner(shouldShow: boolean) {
    this.showExceedingQuestionLimitBanner = shouldShow;
  }

  @action
  _handleQuestionGenerationResponse(event: QuestionGenerationUpdateEvent) {
    this.playgroundApi.updateQuestionGenerationSummary(event);
  }

  @action
  async createPlaygroundGroupWithForm(list: string[]) {
    try {
      let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
        this.intl.t('ai-agent.playground.default-group-names.manual'),
        [],
        undefined,
        undefined,
        list,
      );

      this.trackAddingNewQuestions(list.length, 'add_questions_form');

      this.router.transitionTo(this.routeToTransitionTo, createdGroupSummary.id, {
        queryParams: {
          // We can skip the summary load because we just created the group
          // so we know we have an up to date summary
          skipSummaryLoad: true,
        },
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }
  }

  @action
  async createPlaygroundGroupWithCSV(list: string[]) {
    if (list.length > this.questionLimit) {
      this.updateShowExceedingQuestionLimitBanner(true);
      list = list.slice(0, this.questionLimit);
    }

    try {
      let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
        this.intl.t('ai-agent.playground.default-group-names.csv'),
        [],
        undefined,
        undefined,
        list,
      );

      this.trackAddingNewQuestions(list.length, 'upload_csv_modal');

      this.router.transitionTo(this.routeToTransitionTo, createdGroupSummary.id, {
        queryParams: {
          // We can skip the summary load because we just created the group
          // so we know we have an up to date summary
          skipSummaryLoad: true,
        },
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }
  }

  @action
  async createPlaygroundGroupViaQuestionGeneration(extractionType: ExtractionType) {
    try {
      let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
        this.intl.t('ai-agent.playground.default-group-names.question-generation'),
        [],
        undefined,
        extractionType,
      );

      this.router.transitionTo(this.routeToTransitionTo, createdGroupSummary.id, {
        queryParams: {
          // We can skip the summary load because we just created the group
          // so we know we have an up to date summary
          skipSummaryLoad: true,
        },
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }
  }

  @action
  openUploadCSVModal() {
    this.showUploadCSVModal = true;
  }

  @action
  showAddQuestionsManually() {
    this.isAddingQuestions = true;
  }

  trackAddingNewQuestions(new_question_count: number, source: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'added_questions',
      object: 'playground',
      new_question_count,
      source,
    });
  }

  @action
  selectAddQuestionDropdownOption(option: string) {
    switch (option) {
      case 'generate-questions':
        this.createPlaygroundGroupViaQuestionGeneration(ExtractionType.All);
        break;
      case 'upload-csv':
        this.showUploadCSVModal = true;
        break;
      case 'add-question':
        this.isAddingQuestions = true;
        break;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::GroupsEmptyState': typeof GroupsEmptyState;
    'ai-agent/playground/groups-empty-state': typeof GroupsEmptyState;
  }
}
